/**
 * bulletins/{id} — Phase 3 Director-broadcast announcements.
 *
 * Different beast from /messages: bulletins are broad studio
 * announcements ("studio is closed Monday for the holiday") with no
 * reply thread. They can still require acknowledgement, so Director can
 * tell who has acted without turning the bulletin into a chat.
 *
 * Schema:
 *   title           short bold line
 *   body            optional longer text
 *   pinned          boolean — pinned bulletins float to the top of the
 *                   stack regardless of recency
 *   expiresAt       Timestamp or null. Client hides expired bulletins;
 *                   Director can still see them on the manage page.
 *   publishAt       Timestamp. Families see it after this time; staff can
 *                   see scheduled announcements before they go live.
 *   audienceRoles   string[]; empty = everyone. Otherwise only users
 *                   whose role is in the list see it.
 *   createdAt / createdBy / updatedAt
 */

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage';
// Not firebase/storage directly: the wrapper marks an upload in flight so the
// version auto-update cannot reload the tab mid-send (see storageUpload.ts).
import { uploadBytes } from './storageUpload';
import { auth, db, storage } from './firebase';
import { useAuth } from './auth-context';
import { isStaffRole, useUserDoc, type UserRole } from './users';
import { notifyAudience, notifyUsers } from './notifications';
import type { ClassPostImportantDate } from './classPosts';
import {
  MAX_MESSAGE_ATTACHMENTS,
  type MessageAttachment,
} from './threads';
import { saveAttachmentMessage } from './messageAttachmentWrite';
import {
  bulletinAckedAtCurrentEpoch,
  bulletinActedOnBy as bulletinActedOnByEpoch,
  bulletinAttentionBucket,
  bulletinDismissedAtCurrentEpoch,
} from './bulletinEpoch';
import { fieldsMatchQuery } from './fuzzyMatch';
import { isLateBulletinRecipient } from './bulletinLateRecipient';
import {
  asksForDancerLinkedBulletins,
  fetchDancerLinkedBulletins,
  joinBulletinAsLateRecipient,
  markDancerLinkedBulletinSeen,
  STAFF_PARENT_BULLETINS_CHANGED_EVENT,
} from './staffParentBulletins';

export interface BulletinDoc {
  id: string;
  title: string;
  body: string;
  /**
   * Single inline photo URL pulled from Firebase Storage. Optional —
   * a text-only bulletin still ships fine. We deliberately keep it to
   * ONE photo to preserve the at-a-glance feel; the richer multi-photo
   * surface is what /newsletters is for.
   */
  imageUrl: string | null;
  /** Up to four photos, videos, or safe files attached to the announcement. */
  attachments?: MessageAttachment[];
  pinned: boolean;
  expiresAt: Timestamp | null;
  /** Hidden from families until this time. Missing means live now for older bulletins. */
  publishAt?: Timestamp | FieldValue;
  audienceRoles: UserRole[];
  /** Dates this bulletin should surface in calendars and message summaries. */
  importantDates?: ClassPostImportantDate[];
  /** When true, listed recipients must acknowledge or dismiss. */
  ackRequired?: boolean;
  /** Concrete users in the role audience at publish time, excluding author. */
  expectedRecipientUids?: string[];
  /** Per-recipient acknowledgement timestamps, keyed by uid. */
  acks?: Record<string, Timestamp | FieldValue>;
  /** Per-recipient dismissal timestamps, keyed by uid. */
  dismissedBy?: Record<string, Timestamp | FieldValue>;
  /**
   * The resendEpoch each uid's acks/dismissedBy entry was recorded at.
   * Absent for a uid means epoch 0 - either recorded before this feature
   * existed, or before the bulletin's first Show again. See
   * bulletinEpoch.ts for how these turn into "has this person acted on
   * the CURRENT reactivation."
   */
  ackEpochs?: Record<string, number>;
  dismissEpochs?: Record<string, number>;
  /** Facebook-style reactions: one emoji per uid (see REACTION_EMOJI in classPosts). */
  reactions?: Record<string, string>;
  /**
   * Show again provenance. Set only once this bulletin has been
   * reactivated at least once; the sender shown stays the original
   * createdBy, this is just the honest "shown again" note. resendEpoch
   * starts absent (reads as 0) and increments by exactly one per Show
   * again (see shared/firestore.rules, the bulletins update lane).
   */
  resentAt?: Timestamp | FieldValue;
  resentBy?: string;
  resendEpoch?: number;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  updatedAt: Timestamp | FieldValue;
  /**
   * BROWSER ONLY, never stored. True on a family announcement a staff parent
   * reads through her dancer link (B14, lib/staffParentBulletins.ts): the
   * rules refuse her directly, the server lane proved the link, and her
   * Got it goes back through that lane (dismissBulletinAsViewer below).
   */
  viaDancerLink?: boolean;
}

export interface UseBulletinsResult {
  loading: boolean;
  bulletins: BulletinDoc[];
  error: string | null;
}

/**
 * Subscribe to every bulletin we're allowed to read (rule filters on
 * audience). Sort: pinned first, then newest first.
 */
function timestampLikeMs(value: Timestamp | FieldValue | null | undefined): number {
  const maybeTimestamp = value as Timestamp | null | undefined;
  return typeof maybeTimestamp?.toMillis === 'function'
    ? maybeTimestamp.toMillis()
    : 0;
}

export function bulletinPublishMs(bulletin: Pick<BulletinDoc, 'publishAt'>): number {
  return timestampLikeMs(bulletin.publishAt);
}

export function isBulletinLive(
  bulletin: Pick<BulletinDoc, 'publishAt'>,
  nowMs = Date.now()
): boolean {
  const publishMs = bulletinPublishMs(bulletin);
  return publishMs === 0 || publishMs <= nowMs;
}

export function isBulletinScheduled(
  bulletin: Pick<BulletinDoc, 'publishAt'>,
  nowMs = Date.now()
): boolean {
  const publishMs = bulletinPublishMs(bulletin);
  return publishMs > nowMs;
}
/**
 * How many bulletins the default listeners sync (perf round, audit F3).
 * The Director branch used to be a bare unbounded listener over the whole
 * collection; newest-first limit(100) covers every "recent" surface
 * (Landing strip, active list, attention stack) with years of headroom.
 * Surfaces that genuinely need deep history (the /bulletins "Older
 * announcements" tail, and the /messages Past section's expired/aged-out
 * announcements) pass BULLETIN_HISTORY_CAP instead of shrinking everyone
 * else's read. limit() needs no new index - the single-field orderBy and
 * the two existing composite slices are unchanged. /messages defers its
 * own deep read with useArchivedBulletins' `enabled` flag so it only
 * fires once the viewer opens Past, not on every page load.
 */
/** B60: late-joiner asks already sent from this tab, `${uid}:${bulletinId}`. */
const lateJoinAsked = new Set<string>();

export const BULLETIN_RECENT_CAP = 100;
export const BULLETIN_HISTORY_CAP = 1000;

export function useAllBulletins(
  maxDocs = BULLETIN_RECENT_CAP,
  /**
   * Lazy gate. When false, this hook opens NO listener at all - not "opens
   * one and ignores it," genuinely skipped, so a caller that only wants the
   * deep BULLETIN_HISTORY_CAP read once the viewer actually asks for it
   * (Messages.tsx's Past section, opened only when its own disclosure is
   * open) never pays for it up front. Defaults true so every existing
   * caller (the /bulletins page's own archived tail included) is unchanged.
   */
  enabled = true
): UseBulletinsResult {
  // Raw docs come straight from the listeners; the time-based filter (which
  // ticks every 60s) is applied in a useMemo below. Keeping nowMs OUT of the
  // subscription effect stops the whole listener set from tearing down and
  // re-subscribing every minute (which re-seeded state and re-surfaced any
  // transient error each tick).
  const [rawBulletins, setRawBulletins] = useState<BulletinDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const { userDoc, loading: userLoading } = useUserDoc();
  const { user } = useAuth();
  // B14: the family announcements a staff parent reads through her dancer
  // link, from the server lane. Kept apart from rawBulletins so the role
  // listeners below never tear down over them, and so a lane failure can
  // never take her own announcements off the screen.
  const [linkedBulletins, setLinkedBulletins] = useState<BulletinDoc[]>([]);
  const [linkedRevision, setLinkedRevision] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    // A Got it through the lane on any screen re-asks here.
    const bump = () => setLinkedRevision((n) => n + 1);
    window.addEventListener(STAFF_PARENT_BULLETINS_CHANGED_EVENT, bump);
    return () => window.removeEventListener(STAFF_PARENT_BULLETINS_CHANGED_EVENT, bump);
  }, []);

  useEffect(() => {
    if (!enabled || userLoading || !user || !asksForDancerLinkedBulletins(userDoc)) {
      setLinkedBulletins([]);
      return;
    }
    let cancelled = false;
    fetchDancerLinkedBulletins(user, maxDocs)
      .then((rows) => {
        if (!cancelled) setLinkedBulletins(rows as unknown as BulletinDoc[]);
      })
      .catch(() => {
        // Her own announcements still show; the family copies are an extra
        // the next screen open asks for again.
        if (!cancelled) setLinkedBulletins([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userLoading, user?.uid, userDoc?.role, userDoc?.status, maxDocs, linkedRevision]);

  useEffect(() => {
    if (!enabled) {
      setRawBulletins([]);
      setError(null);
      setLoading(false);
      return;
    }
    // The enabled pass itself must announce it is fetching. Without this,
    // flipping enabled from false to true (Messages.tsx's Past section
    // opening) leaves `loading` sitting at the false the disabled branch
    // just set, straight through until the listener's first snapshot -
    // a caller reads "not loading" while a real fetch is in flight, and a
    // count read during that window (e.g. "0 past announcements") is
    // confidently wrong rather than honestly still-loading (P2 review
    // finding, #618).
    setLoading(true);
    if (userLoading) {
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    if (!userDoc) {
      setRawBulletins([]);
      setLoading(false);
      return;
    }

    // Security rules gate bulletin reads per document by audienceRoles, and
    // Firestore can only allow a LIST query it can prove from the query's own
    // filters. A bare orderBy over the whole collection is unprovable for
    // everyone but the Director, so non-Director accounts subscribe to two
    // provable slices instead: everyone-bulletins (audienceRoles == []) and
    // bulletins that name their own role.
    const base = collection(db, 'bulletins');
    const queries =
      userDoc.role === 'director'
        ? [query(base, orderBy('createdAt', 'desc'), limit(maxDocs))]
        : [
            query(
              base,
              where('audienceRoles', '==', []),
              orderBy('createdAt', 'desc'),
              limit(maxDocs)
            ),
            query(
              base,
              where('audienceRoles', 'array-contains', userDoc.role),
              orderBy('createdAt', 'desc'),
              limit(maxDocs)
            ),
          ];

    const byId = new Map<string, BulletinDoc>();
    const seeded = queries.map(() => false);
    const publish = () => {
      if (!seeded.every(Boolean)) return;
      // Publish ALL docs unfiltered; the live/scheduled + sort pass happens
      // in the useMemo so the 60s tick never re-runs this subscription.
      setRawBulletins(Array.from(byId.values()));
      setLoading(false);
    };

    const unsubs = queries.map((q, i) => {
      const mine = new Set<string>();
      return onSnapshot(
        q,
        (snap) => {
          const next = new Set<string>();
          for (const d of snap.docs) {
            byId.set(d.id, { id: d.id, ...(d.data() as Omit<BulletinDoc, 'id'>) });
            next.add(d.id);
          }
          for (const id of mine) {
            if (!next.has(id)) byId.delete(id);
          }
          mine.clear();
          for (const id of next) mine.add(id);
          seeded[i] = true;
          publish();
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [userDoc?.role, userDoc?.status, userLoading, maxDocs, enabled]);

  // B60: a must-acknowledge announcement published before she was linked
  // (or hired) never listed her, so nothing asked her for it. When the
  // audience covers her role and it is still current, ask the lane to add
  // her to its list (lib/bulletinLateRecipient.ts); the listener above then
  // brings the updated row and every surface treats her as a recipient. No
  // alert of any kind. Asked once per announcement per sign-in per tab, again
  // on a later snapshot only after passing trouble.
  useEffect(() => {
    if (!user || !userDoc || rawBulletins.length === 0) return;
    const viewer = { uid: user.uid, role: userDoc.role, status: userDoc.status };
    // The 60s tick, not Date.now(): a scheduled announcement going live
    // changes no document, so the tick is what brings the ask.
    for (const bulletin of rawBulletins) {
      const key = `${user.uid}:${bulletin.id}`;
      if (lateJoinAsked.has(key) || !isLateBulletinRecipient(bulletin, viewer, nowMs)) continue;
      lateJoinAsked.add(key);
      // Best-effort: on a failure she still reads it exactly as before. Passing
      // trouble (network, 429, 5xx) frees the ask for a later snapshot; a
      // refusal keeps it, so a refused caller never loops.
      joinBulletinAsLateRecipient(user, bulletin.id)
        .then((result) => {
          if (result.retry) lateJoinAsked.delete(key);
        })
        .catch(() => lateJoinAsked.delete(key));
    }
  }, [rawBulletins, user, userDoc, nowMs]);

  // Time-based filter + sort. Recomputes on every 60s tick WITHOUT touching
  // the listeners. Staff/Director see scheduled (not-yet-live) bulletins;
  // everyone else sees only live ones.
  const bulletins = useMemo(() => {
    const canSeeScheduled =
      !!userDoc && userDoc.status === 'active' && isStaffRole(userDoc.role);
    const docs = canSeeScheduled
      ? rawBulletins.slice()
      : rawBulletins.filter((bulletin) => isBulletinLive(bulletin, nowMs));
    // A family announcement reaches her the way it reaches a family: only
    // once it is live, whatever her staff role may preview of her own.
    const ownIds = new Set(rawBulletins.map((bulletin) => bulletin.id));
    for (const bulletin of linkedBulletins) {
      if (!ownIds.has(bulletin.id) && isBulletinLive(bulletin, nowMs)) docs.push(bulletin);
    }
    docs.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      const at = (a.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
      const bt = (b.createdAt as Timestamp | null)?.toMillis?.() ?? 0;
      return bt - at;
    });
    return docs;
  }, [rawBulletins, linkedBulletins, nowMs, userDoc?.role, userDoc?.status, userDoc]);

  return { loading, bulletins, error };
}

/**
 * How long a bulletin floats at the top of "Needs attention" stacks
 * (Family home + Landing strip) before it auto-archives. Per Bobbie:
 * a "studio closed Monday" bulletin from six weeks ago doesn't need
 * to keep hovering — but we don't delete it; it just moves into the
 * "Older announcements" tail on /bulletins.
 *
 * Tunable here if 6 weeks turns out to be the wrong window.
 */
export const BULLETIN_HOVER_WEEKS = 6;
const BULLETIN_HOVER_MS = BULLETIN_HOVER_WEEKS * 7 * 24 * 60 * 60 * 1000;

/**
 * The moment a bulletin's hover window is measured FROM: when it was
 * created, or when it was last shown again, whichever is later.
 *
 * Show again reactivates the ORIGINAL document rather than minting a copy
 * (DECISIONS.md, "Resend reactivates the original, never a copy from the
 * resender"), so the only thing that can make a reactivated announcement
 * young again is `resentAt`. Before this existed, both the hover-window
 * filter below and isBulletinArchived() anchored on `createdAt` alone, and
 * a Show again on anything older than BULLETIN_HOVER_WEEKS re-fired the
 * whole audience's notifications and reset their acted-on state while the
 * banner stayed buried in the archived tail on every screen - and, since
 * the row still read as archived, it kept offering the button, inviting a
 * second and third blast. That is the exact case the feature was built for
 * (the ten-week-old nationals announcement), so it is the case the anchor
 * has to cover (PR #625 review finding 1).
 *
 * A resent bulletin is therefore live again for a full hover window from
 * `resentAt`, then re-archives normally, and this is the ONE place that
 * decision is made: every predicate that ages a bulletin reads it through
 * here so the live filter and the archived predicate cannot drift into
 * disagreeing about which pile a row belongs in.
 *
 * Returns 0 when neither stamp has resolved yet (a pending
 * serverTimestamp() in a local snapshot), which callers read as "not yet a
 * real, dated bulletin" exactly as they did for a pending createdAt.
 *
 * Honest limit, unchanged by this: the live surfaces read
 * BULLETIN_RECENT_CAP bulletins ordered by createdAt, so reactivating a
 * bulletin that already sits deeper than that cap in creation order brings
 * it back to /bulletins' archived-to-active move and the Past sections
 * (both of which read BULLETIN_HISTORY_CAP) but not into the recent-cap
 * strip. At today's volumes nothing is near either cap.
 */
export function bulletinRecencyMs(
  bulletin: Pick<BulletinDoc, 'createdAt' | 'resentAt'>
): number {
  const createdMs = timestampLikeMs(bulletin.createdAt);
  const resentMs = timestampLikeMs(bulletin.resentAt);
  return resentMs > createdMs ? resentMs : createdMs;
}

/**
 * Bulletins still active for the signed-in user:
 *   - not expired (explicit expiresAt in the future), AND
 *   - created OR last shown again within the last BULLETIN_HOVER_WEEKS
 *     (auto-archive after that).
 *
 * Mounted on the Landing card so they surface without requiring the
 * user to navigate to a "news" page.
 */
export function useActiveBulletins(): UseBulletinsResult {
  const { loading, bulletins, error } = useAllBulletins();
  const visible = useMemo(() => {
    const now = Date.now();
    const archiveCutoff = now - BULLETIN_HOVER_MS;
    return bulletins.filter((b) => {
      if (b.expiresAt) {
        const exp = (b.expiresAt as Timestamp).toMillis?.() ?? 0;
        if (exp <= now) return false;
      }
      // Auto-archive past the hover window, measured from the last Show
      // again when there has been one. Must stay the exact complement of
      // isBulletinArchived() below, or the Show again button appears on a
      // row that is already live.
      const recencyMs = bulletinRecencyMs(b);
      if (recencyMs > 0 && recencyMs < archiveCutoff) return false;
      return true;
    });
  }, [bulletins]);
  return { loading, bulletins: visible, error };
}

/**
 * Whether the signed-in uid has already acted on this bulletin - either
 * acknowledged it or dismissed it - AT ITS CURRENT resendEpoch. Only ever
 * true for someone the write rule let into acks/dismissedBy, which is
 * exactly expectedRecipientUids (see isBulletinRecipient() in
 * shared/firestore.rules), so this doubles as "was I a recipient who
 * responded, since the last time this was shown." Exported so every
 * home-screen surface that needs "has this person handled it" shares one
 * answer instead of drifting copies. The epoch arithmetic itself lives in
 * bulletinEpoch.ts (pure, so node --test can pin it directly); this is
 * the re-export every screen already imports from.
 */
export const bulletinActedOnBy = bulletinActedOnByEpoch;
export { bulletinAckedAtCurrentEpoch, bulletinDismissedAtCurrentEpoch };

/**
 * Whether this bulletin is genuinely WAITING ON this person: not yet acted
 * on, AND something they are actually allowed to act on. The second half is
 * the part the home-screen banner forgot, and it produced an announcement
 * Bobbie read five times that "won't go away": the banner counted every
 * un-acted bulletin while the /bulletins page it links to hides ack-required
 * bulletins not aimed at you (nothing to tap) and only offers the seen-style
 * "Got it" when the audience covers your role (the write rule denies it
 * otherwise). A banner pointing at a thing with no button is unclearable by
 * construction.
 *
 * The cases mirror the page and the rules exactly:
 * - ackRequired and you are NOT in expectedRecipientUids: not waiting on
 *   you. The page hides it; the ack write would be denied.
 * - ackRequired and you ARE a recipient: waiting until you ack or dismiss.
 * - not ackRequired: waiting only while your role is in the audience, since
 *   the seen-marker write is scoped to the audience the same way the read
 *   rule is. A Director reading outside the audience can look, not clear.
 *
 * Every surface that COUNTS waiting bulletins must use this, not a local
 * !actedOn filter, or the count and the destination page drift apart again.
 */
/**
 * Whether a studio announcement's audience covers this role. Empty audience
 * means everyone. ONE definition, because three things have to agree or the
 * banner drifts from the page again: the read rule, the seen-marker write
 * rule (both audience-scoped), and every screen deciding whether to offer a
 * clear button. bulletinAwaitsMyAction below is the first caller; Messages'
 * own Done control is the second.
 */
export function bulletinAudienceCoversRole(
  b: Pick<BulletinDoc, 'audienceRoles' | 'viaDancerLink'>,
  role: UserRole | null | undefined
): boolean {
  // B14: a family announcement she reads through her dancer is addressed to
  // her the way it is to the family, and her seen marker goes through the
  // server lane (dismissBulletinAsViewer), so it is hers to clear.
  if (b.viaDancerLink === true) return true;
  return b.audienceRoles.length === 0 || (!!role && b.audienceRoles.includes(role));
}

export function bulletinAwaitsMyAction(
  b: Pick<BulletinDoc, 'acks' | 'dismissedBy' | 'ackEpochs' | 'dismissEpochs' | 'resendEpoch' | 'ackRequired' | 'expectedRecipientUids' | 'audienceRoles' | 'viaDancerLink' | 'reactions'>,
  uid: string | undefined,
  role: UserRole | null | undefined
): boolean {
  if (!uid) return false;
  if (bulletinActedOnBy(b, uid)) return false;
  if (b.ackRequired) {
    return (b.expectedRecipientUids ?? []).includes(uid);
  }
  return bulletinAudienceCoversRole(b, role);
}

export interface UseBulletinAttentionResult extends UseBulletinsResult {
  /**
   * Bulletins the signed-in user has already acknowledged or dismissed -
   * the "Past announcements" tail below the live list, the same shape
   * ClassFeed uses for acted-on posts (FamilyMessagesSection) and Tasks
   * uses for done work. Per person: a bulletin only ever lands here for
   * the uid that actually acted on it, so it keeps showing live for
   * every other expected recipient who has not.
   */
  past: BulletinDoc[];
}

/**
 * Bulletins that should remain in a user's attention stack, split into
 * live (still waiting on you) and past (you already handled it).
 * Ack-required bulletins sink to past once this user acknowledges,
 * dismisses, or taps an emoji on it at the current round (her emoji is her
 * acknowledgement, stamped or not: Bobbie, 2026-10-25, "Count as
 * acknowledged"). Bulletins that do not require acknowledgement
 * default to staying live (there is no button asking for a response) but
 * still sink if this uid's ack/dismiss slot ever gets set anyway - e.g.
 * reactToBulletin() stamps the caller's own ack when they react on
 * /bulletins, even on a bulletin that never required one.
 *
 * The split itself is bulletinAttentionBucket (bulletinEpoch.ts), pulled
 * out to a pure function so DECISIONS.md's "Acknowledged means filed to
 * Past, pinned and expiring included" ruling - pinning never exempts an
 * acked bulletin from Past - is pinned by a plain node test against the
 * real arithmetic, not just this hook's own useMemo.
 */
export function useAttentionBulletins(): UseBulletinAttentionResult {
  const { user } = useAuth();
  const { loading, bulletins, error } = useActiveBulletins();
  const { live, past } = useMemo(() => {
    if (!user) return { live: bulletins, past: [] as BulletinDoc[] };
    const liveList: BulletinDoc[] = [];
    const pastList: BulletinDoc[] = [];
    for (const b of bulletins) {
      const bucket = bulletinAttentionBucket(b, user.uid);
      if (bucket === 'past') pastList.push(b);
      else if (bucket === 'live') liveList.push(b);
      // 'excluded': ack-required but not aimed at this person - never
      // shows either way.
    }
    return { live: liveList, past: pastList };
  }, [bulletins, user]);
  return { loading, bulletins: live, past, error };
}

/**
 * Whether a bulletin belongs in the archived tail: aged past
 * BULLETIN_HOVER_WEEKS measured from bulletinRecencyMs() (creation, or the
 * last Show again if there has been one), or expired outright regardless
 * of age (a bulletin can be young by creation date and still
 * expired-but-young, and that still counts as archived). Its own named,
 * exported predicate - same
 * shape as isBulletinLive/isBulletinScheduled above - so the exact rule
 * behind Bobbie's 2026-08-14 morning ruling ("expired or aged-out never
 * makes a bulletin unreachable, only moves it here") is something a test
 * can drive directly with fabricated timestamps, not logic buried inside a
 * hook's useMemo. Also gates the Show again control (Bulletins.tsx): that
 * control only ever appears on exactly the bulletins this predicate says
 * belong in the tail, the same discipline bulletinAwaitsMyAction documents
 * for the acted-on question.
 *
 * A bulletin that has been shown again is live for a full hover window
 * from its resend and then re-archives normally, because the age here is
 * measured from bulletinRecencyMs(), never from createdAt alone. Reading
 * createdAt directly is what left a reactivated announcement archived
 * forever while its notifications fired anyway (PR #625 review finding 1).
 */
export function isBulletinArchived(
  bulletin: Pick<BulletinDoc, 'createdAt' | 'resentAt' | 'expiresAt'>,
  nowMs = Date.now()
): boolean {
  const archiveCutoff = nowMs - BULLETIN_HOVER_MS;
  const recencyMs = bulletinRecencyMs(bulletin);
  if (recencyMs === 0) return false;
  if (recencyMs >= archiveCutoff) {
    if (bulletin.expiresAt) {
      const exp = (bulletin.expiresAt as Timestamp).toMillis?.() ?? 0;
      return exp > 0 && exp <= nowMs;
    }
    return false;
  }
  return true;
}

/** The one fuzzy bar every announcement search box in the app scores
 *  against - /bulletins' own search, /messages' Past studio announcements
 *  search, and one-search's widened top/Past-reverse searches on /messages
 *  all call bulletinMatchesSearch below rather than picking their own. */
export const BULLETIN_SEARCH_THRESHOLD = 0.6;

/**
 * Title and body, word-fuzzy-matched against the query (fieldsMatchQuery,
 * lib/fuzzyMatch.ts - the same per-word matching Search Past messages uses
 * on every other kind, not a whole-field-only comparison, so a misspelling
 * inside a longer title still clears the bar the same way "rehersal" still
 * finds "rehearsal" everywhere else in this app). Shared by /bulletins' own
 * search box and every /messages announcement search (current top-search
 * widening into Past, the Past studio announcements search, and Search
 * Past messages' own reverse-into-current search) so none of them ever
 * drift into scoring text a different way. Moved here from Bulletins.tsx
 * so it has exactly one home; every page imports it instead of keeping its
 * own copy.
 */
export function bulletinMatchesSearch(b: BulletinDoc, rawQuery: string): boolean {
  return fieldsMatchQuery([b.title, b.body], rawQuery, BULLETIN_SEARCH_THRESHOLD);
}

export interface UseArchivedBulletinsResult extends UseBulletinsResult {
  /**
   * True once the underlying raw read actually hit BULLETIN_HISTORY_CAP -
   * an announcement older than the 1000 most recent could exist and never
   * reach this list. House rule (honest caps): a caller that surfaces this
   * list as a search result must say so rather than imply completeness,
   * instead of a caller reading `bulletins.length` and guessing.
   */
  capped: boolean;
}

/**
 * Bulletins past the hover window but not deleted. Surfaced as a
 * collapsed "Older announcements" tail on /bulletins so history is
 * always accessible, and (Bobbie's 2026-08-14 morning ruling, DECISIONS.md
 * "Announcements are messages: they file into Past and never just
 * vanish") as the expired/aged-out slice of the Past section on
 * /messages.
 *
 * `enabled` lets a caller defer the deep read until it is actually needed
 * - /messages passes pastOpen OR an active top-search query (DECISIONS.md,
 * "Past messages is the complete searchable archive"), so the
 * BULLETIN_HISTORY_CAP listener only opens once the viewer actually taps
 * "Show past messages" or types something to search, not on every
 * /messages page load.
 */
export function useArchivedBulletins(enabled = true): UseArchivedBulletinsResult {
  // Deep history on purpose: this is the /bulletins "Older announcements"
  // tail and the /messages Past announcements slice, the two surfaces that
  // must not silently lose bulletins past the recent cap. Every other
  // surface stays on the cheaper recent-cap read.
  const { loading, bulletins, error } = useAllBulletins(BULLETIN_HISTORY_CAP, enabled);
  const archived = useMemo(() => {
    const nowMs = Date.now();
    return bulletins.filter((b) => isBulletinArchived(b, nowMs));
  }, [bulletins]);
  return { loading, bulletins: archived, error, capped: bulletins.length >= BULLETIN_HISTORY_CAP };
}

export interface NewBulletinInput {
  /** Optional pre-minted id used by the secure attachment gateway. */
  bulletinId?: string;
  title: string;
  body: string;
  imageFile: File | null;
  attachments?: MessageAttachment[];
  pinned: boolean;
  expiresAt: Date | null;
  publishAt?: Date | null;
  audienceRoles: UserRole[];
  ackRequired?: boolean;
  expectedRecipientUids?: string[];
  importantDates?: ClassPostImportantDate[];
}

/** Mint a bulletin id before media upload without writing a document. */
export function newBulletinId(): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'bulletins')).id;
}

/**
 * Pre-mint a Firestore doc ID without writing — lets us scope the
 * Storage path (`bulletin-images/{bulletinId}/…`) to a real id before
 * the doc exists, so the rule we ship pinning uploads to a bulletin
 * id actually has something to bind to.
 *
 * If the user abandons the composer mid-flow, the image is orphaned
 * in Storage. Acceptable for v1; a tiny cleanup Function can sweep
 * later if it ever matters.
 */
export function useCreateBulletin() {
  const { user } = useAuth();
  const { userDoc } = useUserDoc();
  return async (input: NewBulletinInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (userDoc?.role !== 'director' && userDoc?.role !== 'manager') {
      throw new Error('Director or Manager only.');
    }
    if (!input.title.trim()) throw new Error('Title is required.');
    if (
      userDoc.role === 'manager' && (
        input.imageFile !== null
        || input.pinned
        || input.expiresAt !== null
        || input.publishAt != null
        || input.audienceRoles.length !== 0
        || input.ackRequired === true
        || (input.importantDates?.length ?? 0) !== 0
      )
    ) {
      throw new Error('Managers can send a live text announcement to the entire studio.');
    }

    const bulletinId = input.bulletinId ?? newBulletinId();
    const newRef = doc(db, 'bulletins', bulletinId);
    const attachments = (input.attachments ?? []).slice(0, MAX_MESSAGE_ATTACHMENTS);
    if ((input.attachments?.length ?? 0) > MAX_MESSAGE_ATTACHMENTS) {
      throw new Error(`A bulletin can hold up to ${MAX_MESSAGE_ATTACHMENTS} attachments.`);
    }

    let imageUrl: string | null = null;
    if (input.imageFile) {
      imageUrl = await uploadBulletinImage(bulletinId, input.imageFile);
    }
    const publishAt = input.publishAt ?? null;

    const bulletinData = {
      title: input.title.trim(),
      body: input.body.trim(),
      imageUrl,
      ...(attachments.length > 0 ? { attachments } : {}),
      pinned: input.pinned,
      expiresAt: input.expiresAt ?? null,
      publishAt: publishAt ?? serverTimestamp(),
      audienceRoles: input.audienceRoles,
      importantDates: input.importantDates ?? [],
      ackRequired: input.ackRequired === true,
      expectedRecipientUids: Array.from(new Set(input.expectedRecipientUids ?? [])).sort(),
      acks: {},
      dismissedBy: {},
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
    };
    let serverRecipientUids: string[] | null = null;
    if (attachments.length > 0) {
      const saved = await saveAttachmentMessage({
        user,
        action: 'bulletin',
        targetId: bulletinId,
        writeId: bulletinId,
        attachments,
        payload: {
          title: input.title.trim(),
          body: input.body.trim(),
          pinned: input.pinned,
          expiresAt: input.expiresAt,
          publishAt: publishAt ?? new Date(),
          audienceRoles: input.audienceRoles,
          importantDates: input.importantDates ?? [],
          ackRequired: input.ackRequired === true,
          expectedRecipientUids: Array.from(new Set(input.expectedRecipientUids ?? [])).sort(),
        },
      });
      serverRecipientUids = saved.expectedRecipientUids ?? [];
    } else {
      await setDoc(newRef, bulletinData);
    }

    // Fan out a notification for live-now bulletins. Future bulletins stay quiet
    // until server-side scheduled delivery is added.
    if (!publishAt || publishAt.getTime() <= Date.now()) {
      try {
        // bulletinId and the round it went out in are what let a recipient's
        // own Got it, Dismiss or Done clear this card later (live report,
        // 2026-09-24: nothing on the card named the announcement, so it
        // stayed New on the bell forever). A new announcement is round 0.
        const notification = {
          kind: 'bulletin',
          title: input.title.trim(),
          body: input.body.trim().slice(0, 140),
          link: '/bulletins',
          bulletinId,
          resendEpoch: 0,
        } as const;
        if (serverRecipientUids) await notifyUsers(serverRecipientUids, notification);
        else await notifyAudience(input.audienceRoles, notification);
      } catch {
        // ignore: the bulletin is saved; notification delivery is best-effort.
      }
    }
    return bulletinId;
  };
}

/**
 * Upload an inline photo for a bulletin and return its public URL.
 * Storage rule (`storage.rules`) gates this to Director with an
 * image/* content type and a < 8MB size.
 */
export async function uploadBulletinImage(
  bulletinId: string,
  file: File
): Promise<string> {
  if (!storage) throw new Error('Firebase Storage not initialized.');
  // Suffix with a timestamp so a re-upload doesn't collide cache-wise.
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `bulletin-images/${bulletinId}/${Date.now()}-${safeName}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, { contentType: file.type });
  return await getDownloadURL(ref);
}

export async function updateBulletin(
  id: string,
  fields: Partial<Omit<BulletinDoc, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (Array.isArray(fields.attachments) && fields.attachments.length > 0) {
    const user = auth?.currentUser;
    if (!user) throw new Error('Sign in required.');
    await saveAttachmentMessage({
      user,
      action: 'bulletin',
      targetId: id,
      writeId: id,
      attachments: fields.attachments,
      payload: {
        ...(typeof fields.title === 'string' ? { title: fields.title } : {}),
        ...(typeof fields.body === 'string' ? { body: fields.body } : {}),
        ...(typeof fields.pinned === 'boolean' ? { pinned: fields.pinned } : {}),
        ...('expiresAt' in fields ? { expiresAt: fields.expiresAt } : {}),
        ...('publishAt' in fields ? { publishAt: fields.publishAt } : {}),
        ...(Array.isArray(fields.audienceRoles) ? { audienceRoles: fields.audienceRoles } : {}),
        ...(Array.isArray(fields.importantDates) ? { importantDates: fields.importantDates } : {}),
        ...(typeof fields.ackRequired === 'boolean' ? { ackRequired: fields.ackRequired } : {}),
        ...(Array.isArray(fields.expectedRecipientUids)
          ? { expectedRecipientUids: fields.expectedRecipientUids } : {}),
      },
    });
    return;
  }
  await updateDoc(doc(db, 'bulletins', id), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param epoch The bulletin's resendEpoch at the moment of acking (pass
 *   `b.resendEpoch ?? 0`). Stamped alongside the ack so a LATER Show again
 *   can tell this ack is stale without touching the ack itself - see
 *   bulletinEpoch.ts.
 */
export async function ackBulletin(id: string, uid: string, epoch: number): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'bulletins', id), {
    [`acks.${uid}`]: serverTimestamp(),
    [`ackEpochs.${uid}`]: epoch,
  });
}

/**
 * Set or clear the caller's reaction on a studio announcement. Reacting
 * also stamps the caller's ack slot when the bulletin wants one - a
 * reaction counts as the acknowledgment (Bobbie's rule). `epoch` is the
 * bulletin's resendEpoch at the moment of reacting (pass
 * `b.resendEpoch ?? 0`), stamped into ackEpochs in the same write whenever
 * the reaction also acks - same reasoning as ackBulletin above.
 */
export async function reactToBulletin(
  id: string,
  uid: string,
  emoji: string | null,
  alsoAck: boolean,
  epoch: number
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'bulletins', id), {
    [`reactions.${uid}`]: emoji ?? deleteField(),
    ...(alsoAck && emoji
      ? { [`acks.${uid}`]: serverTimestamp(), [`ackEpochs.${uid}`]: epoch }
      : {}),
  });
}

/**
 * @param epoch The bulletin's resendEpoch at the moment of dismissing
 *   (pass `b.resendEpoch ?? 0`). See ackBulletin above.
 */
export async function dismissBulletin(id: string, uid: string, epoch: number): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'bulletins', id), {
    [`dismissedBy.${uid}`]: serverTimestamp(),
    [`dismissEpochs.${uid}`]: epoch,
  });
}

/**
 * The viewer's own "seen" on an announcement, whichever door it came in by.
 * A family announcement a staff parent reads through her dancer (B14) is
 * refused to her by the rules, so it goes through the server lane, which
 * writes the same own-key dismissedBy and dismissEpochs at the current
 * round. Every other announcement is the plain write above. /bulletins' Got
 * it and Dismiss (through bulletinActions.ts) and Messages' Done all call
 * this, so the three cannot disagree about which door a row uses.
 */
export async function dismissBulletinAsViewer(
  b: Pick<BulletinDoc, 'id' | 'resendEpoch' | 'viaDancerLink'>,
  uid: string
): Promise<void> {
  if (b.viaDancerLink === true) {
    const me = auth?.currentUser;
    if (!me || me.uid !== uid) throw new Error('Sign in again, then try that once more.');
    await markDancerLinkedBulletinSeen(me, b.id);
    return;
  }
  await dismissBulletin(b.id, uid, b.resendEpoch ?? 0);
}

/**
 * "Show again" (Bobbie: "we unhide it so it shows back up"). REACTIVATES
 * the same bulletin document rather than minting a copy, so the displayed
 * sender stays the original author (DECISIONS.md, "Resend reactivates the
 * original, never a copy from the resender"): clears expiresAt, stamps
 * resentAt/resentBy for an honest provenance note, and bumps resendEpoch
 * by exactly one. The acks/dismissedBy maps are never touched - history
 * stays history - which is why every "has this person acted on it" check
 * reads resendEpoch instead (bulletinEpoch.ts).
 *
 * Also re-banners the audience: best-effort, matching useCreateBulletin's
 * own notify call exactly (same kind/link, same fallback from the stored
 * expectedRecipientUids to a fresh role-audience resolve). A notify
 * hiccup here must never look like the reactivation itself failed.
 */
export async function showBulletinAgain(
  b: Pick<BulletinDoc, 'id' | 'title' | 'body' | 'audienceRoles' | 'expectedRecipientUids' | 'resendEpoch'>,
  uid: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'bulletins', b.id), {
    expiresAt: null,
    resentAt: serverTimestamp(),
    resentBy: uid,
    resendEpoch: increment(1),
  });
  try {
    // The round this Show again starts: increment(1) above moves resendEpoch
    // from what the Director was looking at to one more. Stamped on the card
    // so an acknowledgement of THIS round clears it, and an acknowledgement
    // of an earlier round never does (lib/bulletinEpoch.ts).
    const notification = {
      kind: 'bulletin',
      title: b.title,
      body: b.body.trim().slice(0, 140),
      link: '/bulletins',
      bulletinId: b.id,
      resendEpoch: (b.resendEpoch ?? 0) + 1,
    } as const;
    const recipientUids = b.expectedRecipientUids ?? [];
    if (recipientUids.length > 0) await notifyUsers(recipientUids, notification);
    else await notifyAudience(b.audienceRoles, notification);
  } catch {
    // ignore: the reactivation above already landed; notification
    // delivery is best-effort, same as useCreateBulletin's create-time send.
  }
}

export async function deleteBulletin(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await deleteDoc(doc(db, 'bulletins', id));
}
